import { BetLineType } from "../graphql/cms-types"
import { BalanceType, BetItemFragment, BetSlipFragment, BetTypeEnum } from "../graphql/core-types"

export const sortBetItems = (
  betItems: (BetItemFragment | null | undefined)[],
  betLineOrder: BetLineType[] | undefined,
): (BetItemFragment | null | undefined)[] => {
  if (!betLineOrder) return betItems

  const sortedItems = betLineOrder.map<(BetItemFragment | null | undefined)[]>(betLineType => {
    return betItems
      .filter(item => item?.type?.toLocaleUpperCase() === betLineType)
      .sort((a, b) => (a?.level || 0) - (b?.level || 0))
  })

  return sortedItems.flat()
}

export const formatBetLineLabel = (type?: string | null): string => {
  if (!type) return ''
  return type
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// rounded down like plc-app so we never overstate a payout
export const formatMultiplier = (multiplier?: string | number | null): string | null => {
  const value = Number(multiplier)
  if (!multiplier || !Number.isFinite(value) || value <= 0) return null
  return `${(Math.floor(value * 100) / 100).toFixed(2)}x`
}

// the payout maths below is ported from plc-app's potentialPayoutCalculation and getFundingTotalsFromBetItems

// every leg of a single game parlay is its own bet item carrying the parlay's amount and multiplier, so count each parlay once
const separateParlaysFromRegularBets = (betItems: (BetItemFragment | null | undefined)[]) => {
  const parlays = new Map<string, BetItemFragment>()
  const regularBetItems: BetItemFragment[] = []
  betItems.forEach((betItem) => {
    if (!betItem) return
    const parlayId = betItem.metadata?.singleGameParlay?.id
    if (parlayId) {
      if (!parlays.has(parlayId)) parlays.set(parlayId, betItem)
    } else {
      regularBetItems.push(betItem)
    }
  })
  return { parlays: Array.from(parlays.values()), regularBetItems }
}

const potentialPayoutCalculation = (betItems: (BetItemFragment | null | undefined)[]): number => {
  const { parlays, regularBetItems } = separateParlaysFromRegularBets(betItems)

  return [...parlays, ...regularBetItems].reduce((total, betItem) => {
    if (betItem.betType === BetTypeEnum.SINGLE_GAME_BOUNTY) {
      const chosenRange = betItem.metadata?.singleGameBounty?.ranges?.[0]
      return total
        + Number(chosenRange?.payoutMultiplier || 0)
        * Number(betItem.betAmount || 0)
        * Number(chosenRange?.bountyTimesAchievable || 0)
    }
    return total + Number(betItem.betAmount || 0) * Number(betItem.payoutMultiplier || 0)
  }, 0)
}

// plc-app's getFundingTotalsFromBetItems: cash (plBalance) and bet credits (plCredits) staked across the slip
export const getFundingTotals = (betItems: (BetItemFragment | null | undefined)[]): { plBalance: number, plCredits: number } => {
  const { parlays, regularBetItems } = separateParlaysFromRegularBets(betItems)

  return [...parlays, ...regularBetItems].reduce((totals, betItem) => {
    const fundingAmount = (wallet: BalanceType) =>
      Number(betItem.fundingSources?.find((source) => source?.wallet === wallet)?.amount || 0)
    return {
      plBalance: totals.plBalance + fundingAmount(BalanceType.PL_BALANCE),
      plCredits: totals.plCredits + fundingAmount(BalanceType.BET_CREDITS),
    }
  }, { plBalance: 0, plCredits: 0 })
}

// what a settled slip paid into each wallet, like plc-app's bet details (getDollarsPayout / getCreditsPayout).
// a slip without the breakdown (the socket's bet_updated payload can lack it) counts its whole payout as cash
export const getPayoutAmounts = (betSlip: BetSlipFragment): { dollars: number, credits: number } => {
  const payoutAmounts = betSlip.payoutAmounts?.filter(Boolean) ?? []
  if (payoutAmounts.length === 0) return { dollars: Number(betSlip.payout || 0), credits: 0 }
  const walletAmount = (wallet: BalanceType) =>
    Number(payoutAmounts.find((payoutAmount) => payoutAmount?.wallet === wallet)?.amount || 0)
  return { dollars: walletAmount(BalanceType.PL_BALANCE), credits: walletAmount(BalanceType.BET_CREDITS) }
}

// slip statuses from the api are pending, completed or cancelled (cancelled means the whole bet was refunded)
export type BetOutcome = 'pending' | 'won' | 'lost' | 'refunded'

// same rule as plc-app's bet history: a completed bet only counts as won when it paid out more than the cash staked
export const getBetOutcome = (betSlip: BetSlipFragment): BetOutcome => {
  const status = (betSlip.status || '').toLowerCase()
  if (status === 'pending') return 'pending'
  if (status === 'cancelled') return 'refunded'
  const { plBalance } = getFundingTotals(betSlip.betItems || [])
  const payout = Number(betSlip.payout || 0)
  return payout > 0 && payout > plBalance ? 'won' : 'lost'
}

// matches plc-app's settled bet toast: net positive only when the payout beats the full amount wagered, credits included
export const isBetNetPositive = (betSlip: BetSlipFragment): boolean =>
  (betSlip.status || '').toLowerCase() === 'completed'
  && Number(betSlip.payout || 0) > Number(betSlip.betTotalAmount || 0)

export const isBetCancelled = (betSlip: BetSlipFragment): boolean =>
  (betSlip.status || '').toLowerCase() === 'cancelled'

// bet credits used as stake aren't paid back out, so they come off the potential payout like in plc-app
export const calculatePotentialPayout = (betSlip: BetSlipFragment): string => {
  const betItems = betSlip.betItems || []
  const { plCredits } = getFundingTotals(betItems)
  return Math.max(0, potentialPayoutCalculation(betItems) - plCredits).toString()
}

export const calculateSlipMultiplier = (betSlip: BetSlipFragment): string | null => {
  const betItems = betSlip.betItems || []
  const { plBalance, plCredits } = getFundingTotals(betItems)
  const totalFunding = plBalance + plCredits
  if (totalFunding <= 0) return null
  return formatMultiplier(potentialPayoutCalculation(betItems) / totalFunding)
}
